import { Avatar, Box, Divider, Sheet, Typography } from "@mui/joy";
import { Fragment, useEffect, useRef, useState } from "react";
import { CustomField, CustomFieldGroup } from "../../entities/customFieldGroup";
import { useUpdateMyCustomFieldMutation } from "../../redux/customField/customFieldEndpoints";
import { useMyCustomFields } from "../../redux/customField/customFieldHooks";
import { GroupCaption } from "../formKit/formKit";
import { CustomFieldRow } from "./customFieldRow";

// Same iOS Settings-style grouped card as the profile page.
const groupCardSx = {
  borderRadius: "14px",
  overflow: "hidden",
  background: "var(--mui-palette-background-surface, #fff)",
  border: "1px solid rgba(120, 120, 128, 0.16)",
} as const;

/**
 * Pending-edit key for one field of one organization.
 *
 * @param organizationID
 * @param noteTypeID
 */
export function customFieldEditKey(
  organizationID: number,
  noteTypeID: number
): string {
  return `${organizationID}:${noteTypeID}`;
}

/**
 * Organization header shown above each group: logo + name, so it's always
 * clear which organization is asking for these fields (values are shared
 * with that org only).
 *
 * @param root0 props
 * @param root0.group the organization's field group
 * @returns {ReactElement} OrganizationCaption
 */
function OrganizationCaption({ group }: { group: CustomFieldGroup }) {
  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 1, px: 0.5, mt: 0.5 }}
    >
      <img
        src={group.organization.logo}
        alt={
          group.organization.name ?? `Organization #${group.organization.id}`
        }
        style={{ height: 22 }}
      />

      <Typography level="body2" sx={{ fontWeight: 600 }}>
        {group.organization.name ?? `Organization #${group.organization.id}`}
      </Typography>
    </Box>
  );
}

/**
 * "Additional Info" block for the bottom of the profile page: the custom
 * fields each of the musician's organizations asks them to fill in, grouped
 * by organization — every group is labeled with the org's logo + name.
 *
 * Editing follows the page's Edit/Save cycle: changes are reported through
 * onChange and saved by the page alongside the rest of the profile.
 *
 * @param root0 props
 * @param root0.edit whether the page is in edit mode
 * @param root0.edits pending values keyed by customFieldEditKey
 * @param root0.onChange records a pending value
 * @returns {ReactElement} CustomFieldsSection
 */
export function CustomFieldsSection({
  edit,
  edits,
  onChange,
}: {
  edit: boolean;
  edits: Record<string, string>;
  onChange: (key: string, value: string) => void;
}) {
  const { groups } = useMyCustomFields();

  if (!groups.length) return <></>;

  return (
    <Box
      sx={{
        maxWidth: 600,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
      }}
    >
      <GroupCaption>Additional Info</GroupCaption>
      {groups.map((group) => (
        <Box
          key={group.organization.id}
          sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}
        >
          <OrganizationCaption group={group} />
          <Sheet variant="plain" sx={groupCardSx}>
            {group.fields.map((field, index) => {
              const key = customFieldEditKey(
                group.organization.id,
                field.noteTypeID
              );
              return (
                <Fragment key={key}>
                  {index > 0 ? <Divider /> : []}
                  <CustomFieldRow
                    field={field}
                    edit={edit}
                    value={edits[key] ?? field.value ?? ""}
                    onChange={(v) => onChange(key, v)}
                  />
                </Fragment>
              );
            })}
          </Sheet>
        </Box>
      ))}
    </Box>
  );
}

/**
 * The custom fields one organization asks the musician to fill in, shown on
 * that organization's page. Always editable; every change saves right away
 * (there is no Edit/Save cycle on that page).
 *
 * @param root0 props
 * @param root0.organizationID the Rhapsody organization behind the page
 * @returns {ReactElement} OrganizationCustomFields
 */
export function OrganizationCustomFields({
  organizationID,
}: {
  organizationID?: number;
}) {
  const { groups } = useMyCustomFields();
  const [updateCustomField] = useUpdateMyCustomFieldMutation();

  const group = organizationID
    ? groups.find((g) => g.organization.id === organizationID)
    : undefined;

  if (!group || !group.fields.length) return <></>;

  return (
    <Box>
      <Typography level="body3">ADDITIONAL INFO</Typography>
      <Typography level="body4">
        Requested by this organization — your answers are shared with them.
      </Typography>
      <Sheet variant="plain" sx={{ ...groupCardSx, mt: 0.5 }}>
        {group.fields.map((field, index) => (
          <Fragment key={field.noteTypeID}>
            {index > 0 ? <Divider /> : []}
            <SelfSavingCustomFieldRow
              organizationID={group.organization.id}
              field={field}
              onSave={(value) =>
                updateCustomField({
                  organizationID: group.organization.id,
                  noteTypeID: field.noteTypeID,
                  value,
                })
              }
            />
          </Fragment>
        ))}
      </Sheet>
    </Box>
  );
}

/**
 * A row that persists on its own: free-typing kinds (text, phone, dates)
 * debounce the save; discrete kinds (switch, rating, select, color) save on
 * every change.
 *
 * @param root0 props
 * @param root0.organizationID the organization the field belongs to
 * @param root0.field the field definition + stored value
 * @param root0.onSave persists the new value
 * @returns {ReactElement} SelfSavingCustomFieldRow
 */
function SelfSavingCustomFieldRow({
  organizationID,
  field,
  onSave,
}: {
  organizationID: number;
  field: CustomField;
  onSave: (value: string) => void;
}) {
  const [value, setValue] = useState(field.value ?? "");
  const saveTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setValue(field.value ?? "");
  }, [field.value, organizationID]);

  useEffect(() => {
    return () => clearTimeout(saveTimeout.current);
  }, []);

  return (
    <CustomFieldRow
      field={field}
      edit
      value={value}
      onChange={(v) => {
        setValue(v);
        clearTimeout(saveTimeout.current);
        saveTimeout.current = setTimeout(() => onSave(v), 800);
      }}
    />
  );
}
